import PropTypes from 'prop-types'
import { discountLine, effectivePriceCents, formatPrice, introLine, priceSuffix } from '../../data/catalog'

// Shared price rendering for plan cards (Billing comparison, sign-up, admin preview): the plan price,
// the running package discount (struck-through base price + what it gives), and the intro period line.
// `tone` 'light' = white cards (Billing, admin), 'dark' = the sign-up's dark cards.
const TONES = {
  light: {
    price: 'text-foreground',
    muted: 'text-muted-foreground',
    strike: 'text-muted-foreground',
    deal: 'text-[#16803F]',
    intro: 'text-[#2563EB]'
  },
  dark: { price: 'text-white', muted: 'text-white/55', strike: 'text-white/45', deal: 'text-[#4ADE80]', intro: 'text-[#93C5FD]' }
}

export const PriceBlock = ({ tier, tone = 'light', priceClass = '', compact = false }) => {
  const t = TONES[tone] ?? TONES.light
  const deal = discountLine(tier)
  const intro = introLine(tier)
  const headline = effectivePriceCents(tier)
  const priceSize = compact ? 'text-[26px]' : 'text-[34px]'
  // "/mo" — the interval alone, for the intro line ("then $1,800/mo").
  const shortSuffix = priceSuffix(tier).replace('/ location ', '').replace(/\s+/g, '')
  return (
    <div>
      <div className='flex flex-wrap items-baseline gap-x-1.5'>
        {deal && (
          <span className={`${compact ? 'text-[14px]' : 'text-[18px]'} line-through ${t.strike}`} aria-label='Regular price'>
            {formatPrice(tier.priceCents)}
          </span>
        )}
        <span className={`${priceSize} font-bold leading-none ${priceClass || t.price}`}>{formatPrice(headline)}</span>
        <span className={`whitespace-nowrap ${compact ? 'text-[12px]' : 'text-[14px]'} ${t.muted}`}>{priceSuffix(tier)}</span>
      </div>
      {deal && (
        <div className={`mt-1 text-[13px] font-semibold ${t.deal}`}>
          {tier.discount?.label ? `${tier.discount.label} · ` : ''}
          {deal}
        </div>
      )}
      {intro && (
        <div className={`mt-1 text-[13px] font-semibold ${t.intro}`}>
          {intro}, then {formatPrice(headline)}
          {shortSuffix}
        </div>
      )}
    </div>
  )
}
PriceBlock.propTypes = {
  tier: PropTypes.object.isRequired,
  tone: PropTypes.oneOf(['light', 'dark']),
  /** Overrides the headline price colour (the recommended card's accent). */
  priceClass: PropTypes.string,
  compact: PropTypes.bool
}
